import type { AccessifyState } from './types'

const HOST_STYLE_ID = 'accessify-host-effects'
export const HOST_WRAPPER_ID = 'accessify-host'
const COLOR_BLIND_FILTER_ID = 'acc-protanopia-filter'

function ensureColorBlindFilter(): void {
  if (document.getElementById(COLOR_BLIND_FILTER_ID)) return
  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg')
  svg.id = COLOR_BLIND_FILTER_ID
  svg.setAttribute('aria-hidden', 'true')
  svg.style.cssText = 'position:absolute;width:0;height:0;pointer-events:none;'
  svg.innerHTML = `
    <defs>
      <filter id="acc-protanopia">
        <feColorMatrix type="matrix" values="
          0.567 0.433 0 0 0
          0.558 0.442 0 0 0
          0     0.242 0.758 0 0
          0     0     0 1 0"/>
      </filter>
    </defs>`
  document.body.appendChild(svg)
}

function ensureHostStyle(): HTMLStyleElement {
  let el = document.getElementById(HOST_STYLE_ID) as HTMLStyleElement | null
  if (!el) {
    el = document.createElement('style')
    el.id = HOST_STYLE_ID
    document.head.appendChild(el)
  }
  return el
}

export function ensureHostWrapper(): HTMLDivElement {
  let wrapper = document.getElementById(HOST_WRAPPER_ID) as HTMLDivElement | null
  if (wrapper) return wrapper

  wrapper = document.createElement('div')
  wrapper.id = HOST_WRAPPER_ID
  const body = document.body
  const moved: Node[] = []
  for (const node of Array.from(body.childNodes)) {
    if (node instanceof HTMLElement && (node.classList.contains('accessify-root') || node.id === HOST_WRAPPER_ID)) {
      continue
    }
    moved.push(node)
  }
  for (const node of moved) wrapper.appendChild(node)
  body.insertBefore(wrapper, body.firstChild)
  return wrapper
}

export function unwrapHost(): void {
  const wrapper = document.getElementById(HOST_WRAPPER_ID)
  if (!wrapper) return
  const body = wrapper.parentElement
  if (!body) return
  while (wrapper.firstChild) body.insertBefore(wrapper.firstChild, wrapper)
  wrapper.remove()
}

const FONT_STEP = 0.1
const SCALE_STEP = 0.1
const LH_STEP = 0.25
const LS_STEP = 0.05

function dynamicCss(state: AccessifyState): string {
  const rules: string[] = []
  const scope = `#${HOST_WRAPPER_ID}`

  if (state.fontSize !== 0) {
    const pct = 100 + state.fontSize * FONT_STEP * 100
    // Scale root html element so rem units (used by modern CSS frameworks, Tailwind, Bootstrap, and demo) scale properly.
    // Also scale host wrapper so inherited typography scales as well.
    rules.push(`html { font-size: ${pct}% !important; }`)
    rules.push(`${scope} { font-size: ${pct}% !important; }`)
  }
  if (state.contentScale !== 0) {
    const factor = 1 + state.contentScale * SCALE_STEP
    rules.push(`body > ${scope} { zoom: ${factor}; }`)
    rules.push(`@supports not (zoom: 1) { body > ${scope} { transform: scale(${factor}); transform-origin: top left; } }`)
  }
  if (state.lineHeight !== 0) {
    const lh = 1.5 + state.lineHeight * LH_STEP
    rules.push(`${scope} * { line-height: ${lh} !important; }`)
  }
  if (state.letterSpacing !== 0) {
    const ls = state.letterSpacing * LS_STEP
    rules.push(`${scope} * { letter-spacing: ${ls}em !important; }`)
  }
  if (state.textAlignment !== 'default') {
    rules.push(`${scope} * { text-align: ${state.textAlignment} !important; }`)
  }
  if (state.textColor) {
    rules.push(`${scope}, ${scope} * { color: ${state.textColor} !important; }`)
  }
  if (state.titleColor) {
    rules.push(`${scope} h1, ${scope} h2, ${scope} h3, ${scope} h4, ${scope} h5, ${scope} h6, ${scope} [class*="title"], ${scope} [class*="heading"] { color: ${state.titleColor} !important; -webkit-text-fill-color: ${state.titleColor} !important; }`)
    rules.push(`${scope} h1 *, ${scope} h2 *, ${scope} h3 *, ${scope} h4 *, ${scope} h5 *, ${scope} h6 *, ${scope} [class*="title"] *, ${scope} [class*="heading"] * { color: ${state.titleColor} !important; -webkit-text-fill-color: ${state.titleColor} !important; }`)
  }
  if (state.backgroundColor) {
    rules.push(`html, body { background-color: ${state.backgroundColor} !important; }`)
    rules.push(`${scope} { background-color: ${state.backgroundColor} !important; }`)
    rules.push(`${scope} header, ${scope} main, ${scope} footer, ${scope} section, ${scope} article, ${scope} aside, ${scope} nav, ${scope} .container, ${scope} .card, ${scope} [class*="card"], ${scope} [class*="hub"], ${scope} [class*="hero"], ${scope} [class*="wrapper"], ${scope} [class*="content"] { background-color: ${state.backgroundColor} !important; }`)
  }
  return rules.join('\n')
}

let magnifierHandler: ((e: MouseEvent) => void) | null = null
let magnifierEl: HTMLDivElement | null = null

function enableMagnifier(): void {
  if (magnifierHandler) return
  magnifierEl = document.createElement('div')
  magnifierEl.className = 'acc-magnify-cursor'
  magnifierEl.style.display = 'none'
  document.body.appendChild(magnifierEl)

  magnifierHandler = (e: MouseEvent) => {
    if (!magnifierEl) return
    const el = document.elementFromPoint(e.clientX, e.clientY)
    if (!el || el.closest('.accessify-root') || el.closest('.acc-magnify-cursor')) {
      magnifierEl.style.display = 'none'
      return
    }
    const text = (el.textContent || '').trim().slice(0, 200)
    if (!text) {
      magnifierEl.style.display = 'none'
      return
    }
    magnifierEl.textContent = text
    magnifierEl.style.display = 'block'
    magnifierEl.style.left = `${Math.min(e.clientX + 14, window.innerWidth - 340)}px`
    magnifierEl.style.top = `${Math.min(e.clientY + 14, window.innerHeight - 80)}px`
  }
  document.addEventListener('mousemove', magnifierHandler)
}

function disableMagnifier(): void {
  if (magnifierHandler) {
    document.removeEventListener('mousemove', magnifierHandler)
    magnifierHandler = null
  }
  if (magnifierEl) {
    magnifierEl.remove()
    magnifierEl = null
  }
}

const LENS_DIAMETER = 260
const LENS_ZOOM = 2.75
const LENS_HALF = LENS_DIAMETER / 2

let lensEl: HTMLDivElement | null = null
let lensInner: HTMLDivElement | null = null
let lensMoveHandler: ((e: MouseEvent) => void) | null = null
let lensScrollHandler: (() => void) | null = null
let lensRefreshTimer: number | null = null
let lensRafId: number | null = null
let lensTargetX = 0
let lensTargetY = 0
let lensCurX = 0
let lensCurY = 0
let lensVisible = false
let lensObserver: MutationObserver | null = null
let lensSnapshotDebounce: number | null = null
// Easing factor — 1 = instant snap, smaller = smoother. 0.35 keeps the
// lens responsive (no obvious lag) while smoothing out mousemove jitter
// and aligning writes to the compositor frame.
const LENS_EASE = 0.35
// Wait this long after the *last* mutation before snapshotting, so a
// burst of DOM changes (e.g. a React re-render touching many nodes)
// produces a single re-snapshot instead of dozens.
const LENS_DEBOUNCE_MS = 150
// Safety polling interval — catches things MutationObserver doesn't
// fire on (video frame advances, CSS transitions, scrollTop changes).
const LENS_POLL_MS = 400

function snapshotHostIntoLens(): void {
  if (!lensInner || !lensEl) return
  const host = document.getElementById(HOST_WRAPPER_ID)
  if (!host) return

  if (typeof window !== 'undefined') {
    const isTransparent = (val?: string) => !val || val === 'transparent' || val === 'rgba(0, 0, 0, 0)'
    const hostStyle = window.getComputedStyle(host)
    const bodyStyle = window.getComputedStyle(document.body)
    const htmlStyle = window.getComputedStyle(document.documentElement)

    const bgColor = !isTransparent(hostStyle.backgroundColor)
      ? hostStyle.backgroundColor
      : !isTransparent(bodyStyle.backgroundColor)
        ? bodyStyle.backgroundColor
        : !isTransparent(htmlStyle.backgroundColor)
          ? htmlStyle.backgroundColor
          : '#ffffff'

    lensEl.style.backgroundColor = bgColor

    const bgImage = (hostStyle.backgroundImage && hostStyle.backgroundImage !== 'none')
      ? hostStyle.backgroundImage
      : (bodyStyle.backgroundImage && bodyStyle.backgroundImage !== 'none')
        ? bodyStyle.backgroundImage
        : (htmlStyle.backgroundImage && htmlStyle.backgroundImage !== 'none')
          ? htmlStyle.backgroundImage
          : ''

    if (bgImage) {
      lensEl.style.backgroundImage = bgImage
      lensEl.style.backgroundSize = bodyStyle.backgroundSize || 'auto'
      lensEl.style.backgroundPosition = bodyStyle.backgroundPosition || '0% 0%'
      lensEl.style.backgroundRepeat = bodyStyle.backgroundRepeat || 'repeat'
    } else {
      lensEl.style.backgroundImage = 'none'
    }
  }

  const rect = host.getBoundingClientRect()
  lensInner.innerHTML = ''
  const clone = host.cloneNode(true) as HTMLElement
  clone.id = HOST_WRAPPER_ID
  clone.style.position = 'absolute'
  clone.style.top = `${rect.top}px`
  clone.style.left = `${rect.left}px`
  clone.style.width = `${rect.width}px`
  clone.style.pointerEvents = 'none'
  // Strip interactive cloned widgets that would re-mount/clash
  clone.querySelectorAll('.accessify-root, .acc-reading-lens, script, iframe').forEach(n => n.remove())
  // Mirror live input/textarea values onto the clone (cloneNode doesn't
  // copy the IDL value property, only the initial defaultValue attribute).
  const liveInputs = host.querySelectorAll<HTMLInputElement | HTMLTextAreaElement>('input, textarea')
  const cloneInputs = clone.querySelectorAll<HTMLInputElement | HTMLTextAreaElement>('input, textarea')
  for (let i = 0; i < liveInputs.length && i < cloneInputs.length; i++) {
    const live = liveInputs[i]
    const dup = cloneInputs[i]
    if (live instanceof HTMLInputElement && dup instanceof HTMLInputElement) {
      dup.value = live.value
      if (live.type === 'checkbox' || live.type === 'radio') dup.checked = live.checked
    } else if (live instanceof HTMLTextAreaElement && dup instanceof HTMLTextAreaElement) {
      dup.value = live.value
    }
  }
  // Same for <select> — cloneNode doesn't preserve the selected option's
  // runtime state.
  const liveSelects = host.querySelectorAll<HTMLSelectElement>('select')
  const cloneSelects = clone.querySelectorAll<HTMLSelectElement>('select')
  for (let i = 0; i < liveSelects.length && i < cloneSelects.length; i++) {
    cloneSelects[i].selectedIndex = liveSelects[i].selectedIndex
  }
  lensInner.appendChild(clone)
}

function scheduleLensSnapshot(): void {
  if (!lensVisible) return
  if (lensSnapshotDebounce !== null) clearTimeout(lensSnapshotDebounce)
  lensSnapshotDebounce = window.setTimeout(() => {
    lensSnapshotDebounce = null
    snapshotHostIntoLens()
  }, LENS_DEBOUNCE_MS)
}

function lensFrame(): void {
  lensRafId = null
  if (!lensEl || !lensInner) return
  // Ease toward the target each frame. Snap when close enough to avoid
  // sub-pixel jitter that the compositor would otherwise have to paint.
  const dx = lensTargetX - lensCurX
  const dy = lensTargetY - lensCurY
  if (Math.abs(dx) < 0.5 && Math.abs(dy) < 0.5) {
    lensCurX = lensTargetX
    lensCurY = lensTargetY
  } else {
    lensCurX += dx * LENS_EASE
    lensCurY += dy * LENS_EASE
    lensRafId = requestAnimationFrame(lensFrame)
  }
  // Single transform on the lens shell (GPU-composited) and another on
  // the inner clone. Both use translate3d so the browser puts them on
  // their own layer and skips layout entirely on each frame.
  lensEl.style.transform = `translate3d(${lensCurX - LENS_HALF}px, ${lensCurY - LENS_HALF}px, 0)`
  const tx = LENS_HALF - lensCurX * LENS_ZOOM
  const ty = LENS_HALF - lensCurY * LENS_ZOOM
  lensInner.style.transform = `translate3d(${tx}px, ${ty}px, 0) scale(${LENS_ZOOM})`
}

function enableReadingLens(): void {
  if (lensEl) return
  lensEl = document.createElement('div')
  lensEl.className = 'acc-reading-lens'
  lensEl.setAttribute('aria-hidden', 'true')
  lensEl.style.display = 'none'
  lensEl.style.left = '0'
  lensEl.style.top = '0'
  lensEl.style.willChange = 'transform'

  lensInner = document.createElement('div')
  lensInner.className = 'acc-reading-lens-inner'
  lensInner.style.transform = `scale(${LENS_ZOOM})`
  lensInner.style.willChange = 'transform'
  lensEl.appendChild(lensInner)

  document.body.appendChild(lensEl)
  snapshotHostIntoLens()

  lensMoveHandler = (e: MouseEvent) => {
    if (!lensEl) return
    const target = e.target as Element | null
    if (target?.closest('.accessify-root')) {
      if (lensVisible) {
        lensEl.style.display = 'none'
        lensVisible = false
      }
      return
    }
    if (!lensVisible) {
      lensEl.style.display = 'block'
      // Skip the ease for the first show — snap to cursor instantly.
      lensCurX = e.clientX
      lensCurY = e.clientY
      lensVisible = true
      // Lens just became visible; re-snapshot now in case anything
      // changed while it was hidden over the widget UI.
      snapshotHostIntoLens()
    }
    lensTargetX = e.clientX
    lensTargetY = e.clientY
    lensRafId ??= requestAnimationFrame(lensFrame)
  }
  document.addEventListener('mousemove', lensMoveHandler, { passive: true })

  // Live re-snapshot on host DOM changes — debounced so a burst of
  // mutations (typical for framework re-renders) only triggers one redraw.
  const host = document.getElementById(HOST_WRAPPER_ID)
  if (host && typeof MutationObserver !== 'undefined') {
    lensObserver = new MutationObserver(scheduleLensSnapshot)
    lensObserver.observe(host, {
      childList: true,
      subtree: true,
      attributes: true,
      characterData: true,
    })
  }

  lensScrollHandler = () => {
    if (lensVisible) snapshotHostIntoLens()
  }
  window.addEventListener('scroll', lensScrollHandler, { passive: true })
}

function disableReadingLens(): void {
  if (lensMoveHandler) {
    document.removeEventListener('mousemove', lensMoveHandler)
    lensMoveHandler = null
  }
  if (lensScrollHandler) {
    window.removeEventListener('scroll', lensScrollHandler)
    lensScrollHandler = null
  }
  if (lensRefreshTimer !== null) {
    clearInterval(lensRefreshTimer)
    lensRefreshTimer = null
  }
  if (lensRafId !== null) {
    cancelAnimationFrame(lensRafId)
    lensRafId = null
  }
  if (lensSnapshotDebounce !== null) {
    clearTimeout(lensSnapshotDebounce)
    lensSnapshotDebounce = null
  }
  if (lensObserver) {
    lensObserver.disconnect()
    lensObserver = null
  }
  lensVisible = false
  if (lensEl) {
    lensEl.remove()
    lensEl = null
    lensInner = null
  }
}

let maskEl: HTMLDivElement | null = null
let maskMoveHandler: ((e: MouseEvent) => void) | null = null

function enableReadingMask(): void {
  if (maskEl) return
  maskEl = document.createElement('div')
  maskEl.className = 'acc-reading-mask'
  maskEl.setAttribute('aria-hidden', 'true')
  maskEl.style.display = 'none'
  document.body.appendChild(maskEl)

  maskMoveHandler = (e: MouseEvent) => {
    if (!maskEl) return
    maskEl.style.display = 'block'
    const y = e.clientY - 45
    maskEl.style.transform = `translate3d(0, ${y}px, 0)`
  }
  document.addEventListener('mousemove', maskMoveHandler)
}

function disableReadingMask(): void {
  if (maskMoveHandler) {
    document.removeEventListener('mousemove', maskMoveHandler)
    maskMoveHandler = null
  }
  if (maskEl) {
    maskEl.remove()
    maskEl = null
  }
}

let guideEl: HTMLDivElement | null = null
let guideMoveHandler: ((e: MouseEvent) => void) | null = null

function positionReadingGuide(clientX: number, clientY: number): void {
  if (!guideEl) return
  const vw = typeof window !== 'undefined' ? window.innerWidth : 1024
  const vh = typeof window !== 'undefined' ? window.innerHeight : 768
  const width = Math.min(480, Math.max(200, vw - 32))
  const height = 16
  const x = Math.max(16, Math.min(vw - width - 16, clientX - width / 2))
  const y = Math.max(0, Math.min(vh - height, clientY - height / 2))
  guideEl.style.transform = `translate3d(${Math.round(x)}px, ${Math.round(y)}px, 0)`
  guideEl.style.display = 'block'
}

function enableReadingGuide(): void {
  if (guideEl) return
  guideEl = document.createElement('div')
  guideEl.className = 'acc-reading-guide'
  guideEl.setAttribute('aria-hidden', 'true')
  document.body.appendChild(guideEl)

  const vw = typeof window !== 'undefined' ? window.innerWidth : 1024
  const vh = typeof window !== 'undefined' ? window.innerHeight : 768
  const initialX = Math.round(vw / 3)
  const initialY = Math.round(vh / 2)
  positionReadingGuide(initialX, initialY)

  guideMoveHandler = (e: MouseEvent) => {
    positionReadingGuide(e.clientX, e.clientY)
  }
  document.addEventListener('mousemove', guideMoveHandler, { passive: true })
}

function disableReadingGuide(): void {
  if (guideMoveHandler) {
    document.removeEventListener('mousemove', guideMoveHandler)
    guideMoveHandler = null
  }
  if (guideEl) {
    guideEl.remove()
    guideEl = null
  }
}

function pauseMedia(root: HTMLElement): void {
  root.querySelectorAll<HTMLMediaElement>('video, audio').forEach(el => {
    try {
      if (!el.paused) el.pause()
    } catch {}
  })
}

let lastDynamicCss: string | null = null

export function applyEffects(state: AccessifyState): void {
  if (typeof document === 'undefined') return
  const wrapper = document.getElementById(HOST_WRAPPER_ID)
  if (!wrapper) return

  const toggles: Array<[string, boolean]> = [
    ['acc-readable-font', state.readableFont],
    ['acc-dyslexia', state.profile === 'dyslexia'],
    ['acc-seizure-safe', state.profile === 'seizure-safe'],
    ['acc-stop-animations', state.stopAnimations],
    ['acc-highlight-titles', state.highlightTitles],
    ['acc-highlight-links', state.highlightLinks],
    ['acc-dark-contrast', state.darkContrast],
    ['acc-light-contrast', state.lightContrast],
    ['acc-high-contrast', state.highContrast],
    ['acc-monochrome', state.monochrome],
    ['acc-invert', state.invertColors],
    ['acc-color-blind', state.colorBlind],
    ['acc-high-saturation', state.highSaturation],
    ['acc-low-saturation', state.lowSaturation],
    ['acc-text-magnifier', state.textMagnifier],
    ['acc-keyboard-nav', state.profile === 'keyboard-navigation'],
    ['acc-screen-reader', state.profile === 'screen-reader'],
    ['acc-highlight-hover', state.highlightHover],
    ['acc-big-black-cursor', state.bigBlackCursor],
    ['acc-big-white-cursor', state.bigWhiteCursor],
  ]
  for (const [cls, on] of toggles) {
    wrapper.classList.toggle(cls, on)
  }
  if (state.profile === 'seizure-safe' || state.stopAnimations) {
    pauseMedia(wrapper)
  }
  document.documentElement.classList.toggle('acc-big-black-cursor', state.bigBlackCursor)
  document.documentElement.classList.toggle('acc-big-white-cursor', state.bigWhiteCursor)

  if (state.colorBlind) ensureColorBlindFilter()
  const nextCss = dynamicCss(state)
  if (nextCss !== lastDynamicCss) {
    ensureHostStyle().textContent = nextCss
    lastDynamicCss = nextCss
  }
  if (state.textMagnifier) enableMagnifier()
  else disableMagnifier()
  if (state.readingLens) {
    enableReadingLens()
    if (lensVisible) snapshotHostIntoLens()
  } else {
    disableReadingLens()
  }
  if (state.readingMask) enableReadingMask()
  else disableReadingMask()
  if (state.readingGuide) enableReadingGuide()
  else disableReadingGuide()
}

export function clearEffects(): void {
  if (typeof document === 'undefined') return
  lastDynamicCss = null
  const wrapper = document.getElementById(HOST_WRAPPER_ID)
  if (wrapper) {
    for (const cls of Array.from(wrapper.classList)) {
      if (cls.startsWith('acc-')) wrapper.classList.remove(cls)
    }
  }
  document.documentElement.classList.remove('acc-big-black-cursor', 'acc-big-white-cursor')
  const host = document.getElementById(HOST_STYLE_ID)
  if (host) host.textContent = ''
  document.getElementById(COLOR_BLIND_FILTER_ID)?.remove()
  disableMagnifier()
  disableReadingLens()
  disableReadingMask()
  disableReadingGuide()
}
